import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { CiMenuKebab } from "react-icons/ci";
import CustomModel from "../../../../components/common/CustomModel";
import CustomSelect from "../../../../components/common/CustomSelect";
import ProspectPropertyService from "../../../Service/ProspectPropertyService";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../components/Constant/Notification";
import { userUid } from "../../../../utils/storage";
import { Divider, MenuItem } from "../../../../components/Constant/Constant";
import {
  FiXCircle,
  FiAlertCircle,
  FiEdit,
  FiCheckCircle,
} from "react-icons/fi";
import {
  getPriorityBadgeColor,
  periotyQueryEnum,
  queryCategories,
} from "../../../Enum/QueryEnum";
import UserService from "../../../../GlobalSetupComponents/Services/UserService";
import {
  formatDate,
  formatDateForInput,
  formatToIndianDate,
} from "../../../../utils/dateFormatter";
import CustomSidebar from "../../../../components/common/CustomSidebar";
import AdvocateService from "../../../Service/AdvocateService";
import MediaUpload from "../../../../components/SetupComponents/PropertiesComponents/MediaUpload";
import { queryUserList } from "../../../../GlobalSetupComponents/Enum/RoleEnum";
import CustomAdvocateList from "./CustomAdvocateList";
import QueriesInfoPage from "./QueriesInfoPage";

const PropertyQueries = ({ propertyId, setPropertyData, propertyData , isDropped, setProgressTrigger }) => {
  const [queries, setQueries] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isResolveModalOpen, setIsResolveModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [actionType, setActionType] = useState(null); // 'resolve', 'ignore', 'drop'
  const [queryLoading, setQueryLoading] = useState(false);
  const [resolveComment, setResolveComment] = useState("");
  const [editFormData, setEditFormData] = useState({
    title: "",
    description: "",
    category: null,
    givenBy: null,
    targetDate: "",
    priority: null,
    associatedLegalOpinion: null,
    files: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("PENDING");
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [users, setUsers] = useState([]);
  const [lastDocId, setLastDocId] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [advocateOpinions, setAdvocateOpinions] = useState([]);
  const [statusCounts, setStatusCounts] = useState({
    pending: 0,
    resolved: 0,
    ignored: 0,
    total: 0,
  });
  const [isQueryInfoOpen, setIsQueryInfoOpen] = useState(false);
  
  // Sidebar stack management
  const [sidebarStack, setSidebarStack] = useState([]);
  const [activeSidebar, setActiveSidebar] = useState(null);
  
  const dropdownRef = useRef(null);
  const observerRef = useRef(null);



  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "",
    givenBy: "",
    givenByName: "",
    targetDate: "",
    priority: "MINOR",
    associatedLegalOpinion: "",
    files: [],
  });

  // Sidebar management functions
  const openSidebar = (sidebarType, data = {}) => {
    // Close current active sidebar if any
    if (activeSidebar) {
      setSidebarStack(prev => [...prev, activeSidebar]);
    }
    
    // Set new active sidebar
    setActiveSidebar({ type: sidebarType, data });
    
    // Open the appropriate sidebar
    switch (sidebarType) {
      case 'addQuery':
        setIsModalOpen(true);
        break;
      case 'editQuery':
        setIsEditModalOpen(true);
        break;
      case 'queryInfo':
        setIsQueryInfoOpen(true);
        break;
      case 'resolveQuery':
        setIsResolveModalOpen(true);
        break;
      default:
        break;
    }
  };

  const closeSidebar = (sidebarType) => {
    // Close the specified sidebar
    switch (sidebarType) {
      case 'addQuery':
        setIsModalOpen(false);
        break;
      case 'editQuery':
        setIsEditModalOpen(false);
        break;
      case 'queryInfo':
        setIsQueryInfoOpen(false);
        break;
      case 'resolveQuery':
        setIsResolveModalOpen(false);
        break;
      default:
        break;
    }

    // Check if there's a previous sidebar in the stack
    if (sidebarStack.length > 0) {
      const previousSidebar = sidebarStack[sidebarStack.length - 1];
      setSidebarStack(prev => prev.slice(0, -1));
      
      // Reopen the previous sidebar after a short delay
      setTimeout(() => {
        setActiveSidebar(previousSidebar);
        switch (previousSidebar.type) {
          case 'addQuery':
            setIsModalOpen(true);
            break;
          case 'editQuery':
            setIsEditModalOpen(true);
            break;
          case 'queryInfo':
            setIsQueryInfoOpen(true);
            break;
          case 'resolveQuery':
            setIsResolveModalOpen(true);
            break;
          default:
            break;
        }
      }, 100);
    } else {
      setActiveSidebar(null);
    }
  };

  const closeAllSidebars = () => {
    setIsModalOpen(false);
    setIsEditModalOpen(false);
    setIsQueryInfoOpen(false);
    setIsResolveModalOpen(false);
    setActiveSidebar(null);
    setSidebarStack([]);
  };

  const fetchAdvocateOpinion = async () => {
    try {
      const response = await AdvocateService.getAdvocateOpinionPropertyId(propertyId);
      setAdvocateOpinions(response.data.data?.advocateAnalysisRecords || []);
    } catch (error) {
      console.error("Error fetching advocate opinion:", error);
    }
  };

  useEffect(() => {
    fetchAdvocateOpinion();
  }, [propertyId]);

  const fetchRoleBasedUsers = async () => {
    try {
      const body = { roleIds: queryUserList };
      const response = await UserService.getUserRoleBasedNames(body);
      
      setUsers(response.data.users || []);
    } catch (error) {
      console.error("Error fetching users:", error);
      showErrorToast("Failed to fetch users.");
    }
  };

  const fetchQueries = async (startAfter = null) => {
    if (!hasMore && startAfter) return;
    setLoading(true);
    setError(null);
    try {
      const response = await ProspectPropertyService.getPropertyQueries({
        propertyId,
        status: activeTab,
        limit: 10,
        startAfter,
      });

      const newQueries = response.data.queries || [];
      setQueries((prev) =>
        startAfter ? [...prev, ...newQueries] : newQueries
      );
      setLastDocId(response.data.pagination.lastDocId);
      setHasMore(response.data.pagination.hasMore);
      setStatusCounts({
        ...response.data.statusCounts,
        total: response.data.pagination.total || 0,
      });
      setPropertyData((prev) => ({
        ...prev,
        queries: startAfter
          ? [...(prev.queries || []), ...newQueries]
          : newQueries,
        statusCounts: response.data.statusCounts,
      }));
    } catch (error) {
      console.error("Error fetching queries:", error);
      setError("Failed to fetch queries.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddQuery = async () => {
    
        if(!formData.category){ 
          showErrorToast("Please select a category");
          return;
        }
        if(formData.category?.value === "Advocate Analysis" && !formData.associatedLegalOpinion?.value){
          showErrorToast("Please select associated legal opinion");
          return;
        }
        
        if(!formData.title.trim()){ 
          showErrorToast("Title cannot be empty");
          return;
        }
        if(!formData.description.trim()){
          showErrorToast("Description cannot be empty");
          return;
        }
        if(!formData.targetDate){
          showErrorToast("Please select a target date");
          return;
        }
        if(!formData.givenBy){ 
          showErrorToast("Please select who given the query");
          return;
        }
        if(!formData.priority){ 
          showErrorToast("Please select a priority");
          return;
        }
        

    setLoading(true);
    setError(null);
    try {
      const queryData = {
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        category: formData.category,
        givenBy: formData.givenBy,
        targetDate: formatDate(formData.targetDate),
        associatedLegalOpinion: formData.associatedLegalOpinion || null,
        fileUrl: formData.files.length > 0 ? formData.files[0] : null,
        createdBy: userUid,
        prospectPropertyId: propertyId,
      };

      const response = await ProspectPropertyService.createPropertyQuery(queryData);
      if (response.status === 200 || response.status === 201) {
        showSuccessToast(response.data.message);
        closeSidebar('addQuery');
        fetchQueries();
      }
      setFormData({
        title: "",
        description: "",
        category: "",
        givenBy: "",
        givenByName: "",
        targetDate: "",
        priority: "MINOR",
        associatedLegalOpinion: "",
        files: [],
      });
    } catch (error) {
      console.error("Error creating query:", error);
      showErrorToast("Failed to create query.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, selected) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selected?.value || "",
      ...(name === "givenBy" ? { givenByName: selected?.label || "" } : {}),
    }));
  };

  const handleMenuClick = (query, action, infoOpen = true) => {
    setActiveDropdown(null);
    setSelectedQuery(query);
    setActionType(action);

    switch (action) {
      case "resolve":
      case "ignore":
      case "drop":
        setResolveComment("");
        openSidebar('resolveQuery', { query, action });
        break;
      case "edit":
        if (query) {
          const selectedUser = users.find((u) => u.value === query.givenBy?.id);
          const selectedPriority = periotyQueryEnum.find((p) => p.value === query.priority);
          const selectedCategory = queryCategories.find((c) => c.value === query.category);
          const selectedOpinion = advocateOpinions.find((o) => o.id === query.associatedLegalOpinion?.id);

          setEditFormData({
            title: query.title || "",
            description: query.description || "",
            priority: selectedPriority
              ? { value: selectedPriority.value, label: selectedPriority.label }
              : null,
            category: selectedCategory
              ? { value: selectedCategory.value, label: selectedCategory.label }
              : null,
            givenBy: selectedUser
              ? { value: selectedUser.value, label: selectedUser.label }
              : null,
            targetDate: formatDateForInput(query.targetDate) || "",
            associatedLegalOpinion: selectedOpinion
              ? {
                  value: selectedOpinion.id,
                  label: `${advocateOpinions.findIndex((o) => o.id === selectedOpinion.id) + 1}. ${selectedOpinion.type}`,
                }
              : null,
            files: query.fileUrl ? [query.fileUrl] : [],
          });
          openSidebar('editQuery', { query });
        }
        break;
      default:
        break;
    }
  };

  const handleConfirmAction = async () => {
    if (!selectedQuery) return;
    let response;
    setQueryLoading(true);
    try {
      switch (actionType) {
        case "resolve":
          if(!resolveComment.trim()){
            showErrorToast("Please provide a reason for resolving the query.");
            return;
          }
          response = await ProspectPropertyService.updatePropertyQueryStatus({
           
            status: "RESOLVED",
            queryId: selectedQuery.id,
            reason: resolveComment,
            modifiedBy: userUid,
          });
          if (response.status === 200 || response.status === 201) {
            showSuccessToast(response.data.message);
              closeSidebar('resolveQuery');
          }
          break;
        case "ignore":
          if(!resolveComment.trim()){
            showErrorToast("Please provide a reason for ignoring the query.");
            return;
          }
          response = await ProspectPropertyService.updatePropertyQueryStatus({
           
            queryId: selectedQuery.id,
            status: "IGNORED",
            reason: resolveComment,
            modifiedBy: userUid,
          });
          if (response.status === 200 || response.status === 201) {
            showSuccessToast(response.data.message);
              closeSidebar('resolveQuery');
          }
          break;
        case "drop":
          if(!resolveComment.trim()){ 
            showErrorToast("Please provide a reason for dropping the query.");
            return;
          }
          response = await ProspectPropertyService.updatePropertyQueryStatus({
           
            queryId: selectedQuery.id,
            status: "DROPPED",
            reason: resolveComment,
            modifiedBy: userUid,
          });
          if (response.status === 200 || response.status === 201) {
            showSuccessToast(response.data.message);
            closeSidebar('resolveQuery');
            // Trigger progress update when query is dropped
            if (setProgressTrigger) {
              setProgressTrigger(prev => !prev);
            }
          }
          break;
      }
    
      setResolveComment("");
      setSelectedQuery(null);
      setActionType(null);
      fetchQueries();
    } catch (error) {
      console.error(`Error performing ${actionType} on query:`, error);
      showErrorToast(`Failed to ${actionType} query.`);
    }finally
    {
      setQueryLoading(false);
    }
  };

  const handleUpdateQuery = async () => {
    if (!selectedQuery) return;

   
       if(!editFormData.category){ 
         showErrorToast("Please select a category");
         return;
       }
       if(editFormData.category?.value === "Advocate Analysis" && !editFormData.associatedLegalOpinion?.value){
         showErrorToast("Please select associated legal opinion");
         return;
       }
       
       if(!editFormData.title.trim()){ 
         showErrorToast("Title cannot be empty");
         return;
       }
       if(!editFormData.description.trim()){
         showErrorToast("Description cannot be empty");
         return;
       }
       if(!editFormData.targetDate){
         showErrorToast("Please select a target date");
         return;
       }
       if(!editFormData.givenBy){ 
         showErrorToast("Please select who given the query");
         return;
       }
       if(!editFormData.priority){ 
         showErrorToast("Please select a priority");
         return;
       }
       

    setLoading(true);
    try {
      const updateData = {
        queryId: selectedQuery.id,
        title: editFormData.title,
        description: editFormData.description,
        priority: editFormData.priority.value,
        category: editFormData.category.value,
        givenBy: editFormData.givenBy.value,
        givenByName: editFormData.givenBy.label,
        fileUrl: editFormData.files.length > 0 ? editFormData.files[0] : null,
        targetDate: formatDate(editFormData.targetDate),
        associatedLegalOpinion: editFormData.associatedLegalOpinion?.value || null,
        modifiedBy: userUid,
      };

      await ProspectPropertyService.updatePropertyQuery(updateData);
      showSuccessToast("Query updated successfully");
      closeSidebar('editQuery');
      setSelectedQuery(null);
      setEditFormData({
        title: "",
        description: "",
        category: null,
        givenBy: null,
        targetDate: "",
        priority: null,
        associatedLegalOpinion: null,
        files: [],
      });
      fetchQueries();
    } catch (error) {
      console.error("Error updating query:", error);
      showErrorToast("Failed to update query.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSelectChange = (name, selected) => {
    setEditFormData((prev) => ({
      ...prev,
      [name]: selected || null,
    }));
  };

  const getActionTitle = () => {
    switch (actionType) {
      case "resolve":
        return "Resolve Query";
      case "ignore":
        return "Ignore Query";
      case "drop":
        return "Drop Property";
      default:
        return "Confirm Action";
    }
  };

  const getActionButtonText = () => {
    switch (actionType) {
      case "resolve":
        return "Resolve";
      case "ignore":
        return "Ignore";
      case "drop":
        return "Drop";
      default:
        return "Confirm";
    }
  };

  const getActionButtonColor = () => {
    switch (actionType) {
      case "resolve":
        return "bg-customGreen hover:bg-green-600";
      case "ignore":
        return "bg-yellow-500 hover:bg-yellow-600";
      case "drop":
        return "bg-red-500 hover:bg-red-600";
      default:
        return "bg-customGreen hover:bg-green-600";
    }
  };

  const toggleDropdown = (queryId) => {
    setActiveDropdown(activeDropdown === queryId ? null : queryId);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          fetchQueries(lastDocId);
        }
      },
      { threshold: 1.0 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
    };
  }, [lastDocId, hasMore, loading]);

  useEffect(() => {
    fetchRoleBasedUsers();
  }, []);

  useEffect(() => {
    if (propertyId) {
      setQueries([]);
      setLastDocId(null);
      setHasMore(true);
      fetchQueries();
    }
  }, [propertyId, activeTab]);

  // Cleanup sidebars when component unmounts or propertyId changes
  useEffect(() => {
    return () => {
      closeAllSidebars();
    };
  }, [propertyId]);

  return (
    <div className="bg-white rounded-[10px] p-5 w-full min-h-full shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="text-[20px] font-medium">
          Queries ({statusCounts.total || 0})
        </div>
        <button
  onClick={() => openSidebar("addQuery")}
  className={`${
    isDropped
      ? "bg-slate-400 cursor-not-allowed"  
      : "bg-customGreen" 
  } text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors`}
  disabled={loading || isDropped}
>
  Add Query
</button>

      </div>

      {/* Status Tabs */}
      <div className="flex mb-5 border-b">
        {[
          { key: "PENDING", label: "Pending", count: statusCounts.pending },
          { key: "RESOLVED", label: "Resolved", count: statusCounts.resolved },
          { key: "IGNORED", label: "Ignored", count: statusCounts.ignored },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => {
              setActiveTab(tab.key)
              toggleDropdown(null);
            }}
            className={`pb-2 px-4 text-base font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? "border-customGreen text-customGreen"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label} ({tab.count || 0})
          </button>
        ))}
      </div>

      {/* Queries List */}
      <div className="max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
        {loading && !queries.length ? (
          <div className="text-center text-gray-600 py-4">Loading...</div>
        ) : queries.length === 0 ? (
          <div className="text-center text-gray-600 py-4">
            No {activeTab.toLowerCase()} queries found.
          </div>
        ) : (
          queries.map((query) => (
            <div
              key={query.id}
              className="bg-[#F6F6F6] rounded-[10px] p-4 mb-4"
            >
              {/* Query Header */}
              <div className="flex items-start justify-between mb-3">
                <div 
                  className="text-[16px] line-clamp-2 text-wrap font-semibold text-gray-800 cursor-pointer hover:text-customGreen transition-colors"
                  onClick={() => {
                    setSelectedQuery(query);
                    openSidebar('queryInfo', { query });
                  }}
                >
                  {query.title}
                </div>
                {activeTab === "PENDING" && (
                  <>
                  {!isDropped && (<div className="relative">
                  <div
                    className="cursor-pointer p-1 hover:bg-gray-200 rounded"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent triggering the parent onClick
                      toggleDropdown(query.id);
                    }}
                  >
                    <CiMenuKebab className="text-lg" />
                  </div>
                  {activeDropdown === query.id && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg z-10">
                      <div className="absolute -top-2 right-4 w-4 h-4 bg-white rotate-45"></div>
                      <div className="py-2">
                        {!(activeTab === "RESOLVED" || activeTab === "DROPPED") && (
                          <>
                            <MenuItem
                              onClick={() => handleMenuClick(query, "resolve")}
                              iconAlt={<FiCheckCircle className="text-green-600" />}
                              label="Resolve"
                            />
                            <Divider />
                            <MenuItem
                              onClick={() => handleMenuClick(query, "ignore")}
                              iconAlt={<FiAlertCircle className="text-yellow-600" />}
                              label="Ignore Query"
                            />
                            <Divider />
                          </>
                        )}
                        <MenuItem
                          onClick={() => handleMenuClick(query, "edit")}
                          iconAlt={<FiEdit className="text-blue-600" />}
                          label="Edit"
                        />
                        <Divider />
                        <MenuItem
                          onClick={() => handleMenuClick(query, "drop")}
                          iconAlt={<FiXCircle className="text-red-600" />}
                          label="Drop Property"
                        />
                      </div>
                    </div>
                  )}
                </div>)}
                  </>
                )}
                
                
              </div>

              {/* Query Description */}
              <div className="text-[14px] text-wrap line-clamp-3 truncate font-normal text-gray-700 mb-3">
                {query.description}
              </div>

              {/* Query Details */}
              <div className="text-[12px] text-[#505050] font-normal mb-3">
                Given by: <span className="font-medium">{query.givenBy.name} ({query.givenBy?.role})</span> 
               
              </div>

              {/* Query Details */}
              <div className="text-[12px] text-[#505050] font-normal mb-3">
                Given by date : <span className="font-medium"> {query.createdTimestamp ? (query.createdTimestamp) : "N/A"}</span> 
               
              </div>


              {activeTab === "RESOLVED" ? (
                <div className="text-[14px] text-blue-500 font-normal mb-3">
                  Solved Date: {query.statusTimestamp ? (query.statusTimestamp) : "N/A"}
                </div>  
              ) : activeTab === "IGNORED" ? (
                <div className="text-[14px] text-blue-500 font-normal mb-3">
                  Ignored Date: {query.statusTimestamp ? (query.statusTimestamp) : "N/A"}
                </div>
              ) : (
                <div className="text-[14px] text-blue-500 font-normal mb-3">
                  Target Date: {query.targetDate ? (query.targetDate) : "N/A"}
                </div>
              )}

              {/* Priority and Category Badges */}
              <div className="flex gap-2">
                <span
                  className={`px-3 py-1 flex items-center gap-2 rounded-full text-xs font-medium ${getPriorityBadgeColor(
                    query.priority
                  )}`}
                >
                  {periotyQueryEnum.find((p) => p.value === query.priority)?.icon || null}
                  {periotyQueryEnum.find((p) => p.value === query.priority)?.label || null}
                </span>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-600`}
                >
                  Category: {query.category}
                </span>
              </div>
            </div>
          ))
        )}
        {hasMore && (
          <div ref={observerRef} className="h-10 flex items-center justify-center">
            {loading && <div className="text-gray-600">Loading more...</div>}
          </div>
        )}
      </div>

      {/* Add Query Modal */}
      <CustomSidebar
        visible={isModalOpen}
        onHide={() => {
          closeSidebar('addQuery')
          setFormData({
            title: "",
            description: "",
            category: "",
            givenBy: "",
            givenByName: "",
            targetDate: "",
            priority: "MINOR",
            associatedLegalOpinion: "",
            files: [],
          });
          
        }}
        width="600px"
        title="Add Query"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-12 items-center">
            <label className="col-span-4 text-sm font-medium text-gray-700">
              Query Category <span className="text-red-500">*</span>
            </label>
            <div className="col-span-8">
              <CustomSelect
                options={queryCategories}
                value={queryCategories.find((opt) => opt.value === formData.category) || null}
                onChange={(selected) => handleSelectChange("category", selected)}
                placeholder="Select Query Category"
              />
            </div>
          </div>
          {formData.category === "Advocate Analysis" && (
            <div className="grid grid-cols-12 items-center">
              <label className="col-span-4 text-sm font-medium text-gray-700">
                Advocate Opinion <span className="text-red-500">*</span>
              </label>
              <div className="col-span-8">
                <CustomAdvocateList
                  advocateOpinions={advocateOpinions}
                  selectedOpinionId={formData.associatedLegalOpinion}
                  onChange={(selected) => handleSelectChange("associatedLegalOpinion", selected)}
                  placeholder="Select Advocate Opinion"
                />
              </div>
            </div>
          )}
          <div className="grid grid-cols-12 items-center">
            <label className="col-span-4 text-sm font-medium text-gray-700">
              Query Title <span className="text-red-500">*</span>
            </label>
            <div className="col-span-8">
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-customGreen"
                placeholder="Enter query title"
              />
            </div>
          </div>
          <div className="grid grid-cols-12 items-center">
            <label className="col-span-4 text-sm font-medium text-gray-700">
              Query Description <span className="text-red-500">*</span>
            </label>
            <div className="col-span-8">
              <textarea
                rows={4}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-customGreen"
                placeholder="Enter query description"
              />
            </div>
          </div>
          <div className="grid grid-cols-12 items-center">
            <label className="col-span-4 text-sm font-medium text-gray-700">
              Query Priority <span className="text-red-500">*</span>
            </label>
            <div className="col-span-8">
              <CustomSelect
                options={periotyQueryEnum}
                value={periotyQueryEnum.find((opt) => opt.value === formData.priority) || null}
                onChange={(selected) => handleSelectChange("priority", selected)}
                placeholder="Select Query Priority"
              />
            </div>
          </div>
          <div className="grid grid-cols-12 items-center">
            <label className="col-span-4 text-sm font-medium text-gray-700">
              Query Given By <span className="text-red-500">*</span>
            </label>
            <div className="col-span-8">
              <CustomSelect
                options={users}
                value={users.find((opt) => opt.value === formData.givenBy) || null}
                onChange={(selected) => handleSelectChange("givenBy", selected)}
                placeholder="Select User"
              />
            </div>
          </div>
          <div className="grid grid-cols-12 items-center">
            <label className="col-span-4 text-sm font-medium text-gray-700">
              Target Date <span className="text-red-500">*</span>
            </label>
            <div className="col-span-8">
              <input
                type="date"
                name="targetDate"
                value={formData.targetDate}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-customGreen"
              />
            </div>
          </div>
          {formData.category !== "Advocate Analysis" && (
            <MediaUpload
              mediaType={"document"}
              files={formData.files || []}
              onChange={(files) => setFormData((prev) => ({ ...prev, files }))}
              accept="application/pdf,application/dwg,application/x-dwg,application/dxf,application/x-dxf"
              maxFiles={1}
              folderpath="AdvocateOpinion"
            />
          )}
          <button
            onClick={handleAddQuery}
            disabled={loading}
            className="w-full py-2 bg-customGreen text-white rounded-md disabled:opacity-50 hover:bg-green-600 transition-colors"
          >
            {loading ? "Submitting..." : "Add Query"}
          </button>
        </div>
      </CustomSidebar>

      {/* Resolve/Ignore/Drop Modal */}
      <CustomModel
        isOpen={isResolveModalOpen}
        isClose={() => {
          closeSidebar('resolveQuery');
          setResolveComment("");
          setSelectedQuery(null);
          setActionType(null);
        }}
        title={getActionTitle()}
        content={
          <div className="space-y-4">
            {selectedQuery && (
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h3 className="font-medium text-gray-800 mb-2">Query Title</h3>
                <p className="text-gray-600 line-clamp-2 truncate text-wrap text-sm mb-2">{selectedQuery.title}</p>
                <p className="text-gray-500 line-clamp-3 truncate text-wrap text-xs">{selectedQuery.description}</p>
                <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                  <span>Given By: {selectedQuery.givenBy.name || "N/A"}</span>
                </div>
                {actionType === "resolve" && (
                  <div className="flex items-center gap-2 mt-2 text-xs">
                    <span>Resolved Date: {selectedQuery?.statusTimestamp ? formatToIndianDate(selectedQuery.statusTimestamp) : "N/A"}</span>
                  </div>
                )}
                <div className="flex gap-2 mt-2">
                  <span
                    className={`px-2 py-1 flex items-center gap-1 rounded-full text-xs font-medium ${getPriorityBadgeColor(
                      selectedQuery.priority
                    )}`}
                  >
                    {periotyQueryEnum.find((p) => p.value === selectedQuery.priority)?.icon ||
                      null}
                    {periotyQueryEnum.find((p) => p.value === selectedQuery.priority)?.label ||
                      null}
                  </span>
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-600">
                    Category: {selectedQuery.category}
                  </span>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                {actionType === "resolve"
                  ? "Resolve Comment"
                  : actionType === "ignore"
                  ? "Ignore Comment"
                  : "Drop Comment"}
              </label>
              <textarea
                rows={4}
                value={resolveComment}
                onChange={(e) => setResolveComment(e.target.value)}
                className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-customGreen"
                placeholder="Type Comment"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <button
                onClick={() => {
                  closeSidebar('resolveQuery');
                  setResolveComment("");
                  setSelectedQuery(null);
                  setActionType(null);
                }}
                className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                disabled={queryLoading}
                onClick={handleConfirmAction}
                className={`flex-1 py-2 text-white rounded-md transition-colors ${getActionButtonColor()}`}
              >
                {getActionButtonText()}
              </button>
            </div>
          </div>
        }
      />

      {/* Edit Query Modal */}
      <CustomSidebar
        visible={isEditModalOpen}
        onHide={() => {
          closeSidebar('editQuery');
          setSelectedQuery(null);
          setEditFormData({
            title: "",
            description: "",
            category: null,
            givenBy: null,
            targetDate: "",
            priority: null,
            associatedLegalOpinion: null,
            files: [],
          });
        }}
        title="Edit Query"
        width="600px"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-12 items-center">
            <label className="col-span-4 text-sm font-medium text-gray-700">
              Query Category <span className="text-red-500">*</span>
            </label>
            <div className="col-span-8">
              <CustomSelect
                options={queryCategories}
                value={editFormData.category}
                onChange={(selected) => handleEditSelectChange("category", selected)}
                placeholder="Select Query Category"
              />
            </div>
          </div>
          {editFormData.category?.value === "Advocate Analysis" && (
            <div className="grid grid-cols-12 items-center">
              <label className="col-span-4 text-sm font-medium text-gray-700">
                Advocate Opinion <span className="text-red-500">*</span>
              </label>
              <div className="col-span-8">
                <CustomAdvocateList
                  advocateOpinions={advocateOpinions}
                  selectedOpinionId={editFormData.associatedLegalOpinion?.value}
                  onChange={(selected) =>
                    handleEditSelectChange("associatedLegalOpinion", selected)
                  }
                  placeholder="Select Advocate Opinion"
                />
              </div>
            </div>
          )}
          <div className="grid grid-cols-12 items-center">
            <label className="col-span-4 text-sm font-medium text-gray-700">
              Query Title <span className="text-red-500">*</span>
            </label>
            <div className="col-span-8">
              <input
                type="text"
                name="title"
                value={editFormData.title}
                onChange={handleEditInputChange}
                className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-customGreen"
                placeholder="Enter query title"
              />
            </div>
          </div>
          <div className="grid grid-cols-12 items-center">
            <label className="col-span-4 text-sm font-medium text-gray-700">
              Query Description <span className="text-red-500">*</span>
            </label>
            <div className="col-span-8">
              <textarea
                rows={4}
                name="description"
                value={editFormData.description}
                onChange={handleEditInputChange}
                className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-customGreen"
                placeholder="Enter query description"
              />
            </div>
          </div>
          <div className="grid grid-cols-12 items-center">
            <label className="col-span-4 text-sm font-medium text-gray-700">
              Query Priority <span className="text-red-500">*</span>
            </label>
            <div className="col-span-8">
              <CustomSelect
                options={periotyQueryEnum}
                value={editFormData.priority}
                onChange={(selected) => handleEditSelectChange("priority", selected)}
                placeholder="Select Query Priority"
              />
            </div>
          </div>
          <div className="grid grid-cols-12 items-center">
            <label className="col-span-4 text-sm font-medium text-gray-700">
              Query Given By <span className="text-red-500">*</span>
            </label>
            <div className="col-span-8">
              <CustomSelect
                options={users}
                value={editFormData.givenBy}
                onChange={(selected) => handleEditSelectChange("givenBy", selected)}
                placeholder="Select User"
              />
            </div>
          </div>
          <div className="grid grid-cols-12 items-center">
            <label className="col-span-4 text-sm font-medium text-gray-700">
              Target Date <span className="text-red-500">*</span>
            </label>
            <div className="col-span-8">
              <input
                type="date"
                name="targetDate"
                value={editFormData.targetDate}
                onChange={handleEditInputChange}
                className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-customGreen"
              />
            </div>
          </div>
          {editFormData.category?.value !== "Advocate Analysis" && (
            <MediaUpload
              mediaType={"document"}
              files={editFormData.files}
              onChange={(files) => setEditFormData((prev) => ({ ...prev, files }))}
              accept="application/pdf,application/dwg,application/x-dwg,application/dxf,application/x-dxf"
              maxFiles={1}
              folderpath="AdvocateOpinion"
            />
          )}
          <button
            onClick={handleUpdateQuery}
            disabled={loading}
            className="w-full py-2 bg-customGreen text-white rounded-md disabled:opacity-50 hover:bg-green-600 transition-colors"
          >
            {loading ? "Updating..." : "Update Query"}
          </button>
        </div>
      </CustomSidebar>

      {/* Query Info Page Modal */}
    
        <CustomSidebar
          visible={isQueryInfoOpen}
          onHide={() => {
            closeSidebar('queryInfo');
            setSelectedQuery(null);
          }}
          title={`Query Details - ${selectedQuery?.title}`}
          width="1300px"
        >
          <QueriesInfoPage
            propertyId={propertyId}
            setPropertyData={setPropertyData}
            propertyData={propertyData}
            isDropped={isDropped}
            handleMenuClick={handleMenuClick}
            queryData={selectedQuery}
            setProgressTrigger={setProgressTrigger}
          />
        </CustomSidebar>
  
    </div>
  );
};

export default PropertyQueries;